/**
 * Para onde cada sistema recebe as notificações (`R-08`, ADR-078): o endereço `https://` da
 * Sugestão de Autuação e o da operadora da TAG, cadastrados pelo administrador.
 *
 * **O segredo da assinatura aparece uma vez**: o cockpit o gera na primeira gravação do
 * endereço e a cada "segredo novo", e ele só volta nesta resposta — guarde-o no sistema que
 * recebe. A tela nunca o lê de volta: o servidor diz só se existe. A trilha leva o nome do
 * servidor, nunca o segredo nem o caminho do endereço.
 */
import { useCallback, useEffect, useState } from "react";

import { ErroDaApi, api } from "../api/cliente";
import type {
  DestinoDoSistema,
  DestinoGravado,
  DestinosDosSistemas,
} from "../api/tipos";
import { Badge, Botao, Estado } from "../componentes/base";
import { dataEHora } from "../formato";

const CAMINHO = "/tollvision/notificacoes-internas/destinos";

function mensagem(falha: unknown, padrao: string): string {
  return falha instanceof ErroDaApi ? falha.message : padrao;
}

export function DestinosDasNotificacoes() {
  const [destinos, definirDestinos] = useState<DestinosDosSistemas | null>(
    null,
  );
  const [erro, definirErro] = useState<string | null>(null);
  // O segredo de uma vez mora aqui, acima da releitura: se ela falhar, ou se a pessoa gravar
  // de novo, ele continua na tela até ser dispensado.
  const [segredos, definirSegredos] = useState<Record<string, string>>({});

  const carregar = useCallback(async () => {
    try {
      definirDestinos(await api<DestinosDosSistemas>(CAMINHO));
      definirErro(null);
    } catch (falha) {
      definirErro(mensagem(falha, "Não foi possível ler os destinos."));
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const segredoNovo = (destinatario: string, segredo: string | null) => {
    if (segredo) definirSegredos((s) => ({ ...s, [destinatario]: segredo }));
  };
  const dispensar = (destinatario: string) =>
    definirSegredos((s) =>
      Object.fromEntries(Object.entries(s).filter(([d]) => d !== destinatario)),
    );

  const mostrados = Object.entries(segredos).map(([destinatario, segredo]) => (
    <SegredoDeUmaVez
      key={destinatario}
      rotulo={
        destinos?.destinos.find((d) => d.destinatario === destinatario)
          ?.rotulo ?? destinatario
      }
      segredo={segredo}
      aoDispensar={() => dispensar(destinatario)}
    />
  ));

  if (erro)
    return (
      <div className="grid gap-3">
        {mostrados}
        <Estado tipo="erro" titulo={erro} />
      </div>
    );
  if (!destinos)
    return <Estado tipo="carregando" titulo="Lendo os destinos…" />;

  return (
    <div className="grid gap-3">
      <p className="max-w-prose text-[11.5px] text-tinta-3">
        Cada notificação a um sistema vai num <b>POST</b> com o corpo em JSON e
        três cabeçalhos: <code className="mono">X-Cockpit-Entrega</code> (o id
        da notificação — a mesma entrega pode chegar mais de uma vez, e o
        sistema deve devolver o mesmo recibo),{" "}
        <code className="mono">X-Cockpit-Evento</code> e{" "}
        <code className="mono">X-Cockpit-Assinatura</code>, no formato{" "}
        <code className="mono">t=&lt;segundos&gt;,v1=&lt;hex&gt;</code>: o
        HMAC-SHA256 do segredo sobre <code className="mono">t.</code> seguido do
        corpo. O sistema confere a assinatura e recusa o instante velho. Uma
        resposta 2xx entrega (o campo <code className="mono">recibo</code> fica
        guardado); sem resposta, 5xx, 408 e 429 o cockpit tenta de novo por até
        24 horas; outro 4xx é recusa, e não insiste.
      </p>
      {destinos.emulador_na_demonstracao && (
        <p className="text-[11.5px] text-tinta-2">
          <Badge cor="cinza">demonstração</Badge> Sem endereço cadastrado, quem
          recebe é o emulador dos sistemas, que se declara: o recibo começa por{" "}
          <code className="mono">EMU-</code> e nada chega a um sistema de
          verdade.
        </p>
      )}
      {mostrados}
      {destinos.destinos.map((d) => (
        <Destino
          key={d.destinatario}
          destino={d}
          aoMudar={carregar}
          aoGerar={(segredo) => segredoNovo(d.destinatario, segredo)}
        />
      ))}
    </div>
  );
}

function SegredoDeUmaVez({
  rotulo,
  segredo,
  aoDispensar,
}: {
  rotulo: string;
  segredo: string;
  aoDispensar: () => void;
}) {
  return (
    <div
      role="note"
      aria-label={`O segredo de ${rotulo}`}
      className="grid gap-1 rounded-cartao border border-amarelo-borda bg-amarelo-fundo px-3 py-2"
    >
      <p className="text-[11.5px] font-semibold text-amarelo">
        O segredo da assinatura de {rotulo} aparece só agora. Guarde-o no
        sistema que recebe: ele não volta a aparecer.
      </p>
      <code className="mono text-[12px] break-all text-tinta">{segredo}</code>
      <div>
        <Botao onClick={aoDispensar}>Já guardei</Botao>
      </div>
    </div>
  );
}

function Destino({
  destino,
  aoMudar,
  aoGerar,
}: {
  destino: DestinoDoSistema;
  aoMudar: () => Promise<void>;
  aoGerar: (segredo: string | null) => void;
}) {
  const [url, definirUrl] = useState(destino.url ?? "");
  const [aviso, definirAviso] = useState<string | null>(null);
  const [gravando, definirGravando] = useState(false);

  useEffect(() => definirUrl(destino.url ?? ""), [destino.url]);

  const fazer = async (
    pedido: () => Promise<DestinoGravado | DestinosDosSistemas>,
    feito: string,
  ) => {
    definirGravando(true);
    try {
      const r = await pedido();
      if ("segredo" in r) aoGerar(r.segredo ?? null);
      definirAviso(feito);
      await aoMudar();
    } catch (falha) {
      definirAviso(mensagem(falha, "Não foi possível."));
    } finally {
      definirGravando(false);
    }
  };

  const caminho = `${CAMINHO}/${destino.destinatario}`;
  const modo =
    destino.modo === "configurado"
      ? { cor: "verde" as const, texto: "endereço cadastrado" }
      : destino.modo === "emulador"
        ? { cor: "cinza" as const, texto: "emulador da demonstração" }
        : { cor: "vermelho" as const, texto: "sem endereço" };

  return (
    <section
      aria-label={destino.rotulo}
      className="grid gap-2 rounded-cartao border border-linha bg-painel p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-[13px] font-bold text-tinta">{destino.rotulo}</h3>
        <Badge cor={modo.cor}>{modo.texto}</Badge>
        {destino.segredo_definido && (
          <Badge cor="azul">assinatura com segredo</Badge>
        )}
        {destino.atualizado_em && (
          <span className="ml-auto text-[11px] text-tinta-3">
            Alterado por {destino.atualizado_por ?? "—"} em{" "}
            {dataEHora(destino.atualizado_em)}
          </span>
        )}
      </div>
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void fazer(
            () =>
              api<DestinoGravado>(caminho, {
                method: "PUT",
                corpo: { url },
              }),
            "Endereço gravado.",
          );
        }}
      >
        <label className="grid min-w-[280px] flex-1 gap-1 text-[11px] text-tinta-3">
          Endereço (https://)
          <input
            value={url}
            onChange={(e) => definirUrl(e.target.value)}
            placeholder="https://sistema.concessionaria.com.br/notificacoes"
            maxLength={500}
            className="mono min-h-8 rounded-botao border border-linha bg-painel-2 px-2 text-[16px] text-tinta md:text-[11.5px]"
          />
        </label>
        <Botao
          papel="primaria"
          type="submit"
          disabled={gravando || !url.trim()}
        >
          Gravar
        </Botao>
        {destino.url && (
          <>
            <Botao
              disabled={gravando}
              title="O segredo antigo deixa de valer na hora: o sistema do outro lado precisa do novo."
              onClick={() => {
                if (
                  !window.confirm(
                    `Gerar um segredo novo para ${destino.rotulo}? O atual para de valer agora: até o sistema receber o novo, ele recusa as notificações, e o cockpit insiste por 24 horas.`,
                  )
                )
                  return;
                void fazer(
                  () =>
                    api<DestinoGravado>(`${caminho}/segredo`, {
                      method: "POST",
                    }),
                  "Segredo novo gerado — o anterior parou de valer.",
                );
              }}
            >
              Gerar segredo novo
            </Botao>
            <Botao
              papel="perigo"
              disabled={gravando}
              onClick={() => {
                if (
                  !window.confirm(
                    `Tirar o endereço de ${destino.rotulo}? As notificações a ele ficam sem endereço — nada se perde, e a supervisão reenvia depois de um endereço novo.`,
                  )
                )
                  return;
                void fazer(
                  () => api<DestinosDosSistemas>(caminho, { method: "DELETE" }),
                  "Endereço retirado: as notificações a este sistema ficam sem endereço até um novo.",
                );
              }}
            >
              Tirar o endereço
            </Botao>
          </>
        )}
      </form>
      {aviso && (
        <p role="status" className="text-[11.5px] text-tinta-2">
          {aviso}
        </p>
      )}
    </section>
  );
}
